import * as THREE from 'three/webgpu';
import type { FeatureCurve } from '../../mesh/feature';
import { normalizeVec3 } from './PointFeatureSolveContextGeometryUtils';

/**
 * 读取特征曲线的世界坐标采样点。
 *
 * 设计说明：
 * 1. 基准点与基准轴都会从圆边采样点恢复圆心和法向；
 * 2. 采样点必须在当前 `matrixWorld` 下读取，才能在装配体移动或旋转后重建到新位置；
 * 3. 该函数只负责曲线顶点到世界坐标的转换，不推断圆参数。
 *
 * @param curve 特征曲线对象。
 * @returns 世界坐标采样点列表；曲线数据不足时返回空数组。
 */
export function readFeatureCurveWorldSamplePoints(curve: FeatureCurve): THREE.Vector3[] {
  const sampledPoints = curve.getWorldSamplePoints();
  if (sampledPoints.length < 3) {
    return [];
  }

  return sampledPoints;
}

/**
 * 从采样点中挑选三个稳定点用于拟合圆。
 *
 * 设计说明：
 * 1. 圆边采样可能包含重复点或非常接近的端点，不能固定使用前三个点；
 * 2. 这里固定保留第一个点作为参数起点，再向中间和末尾搜索非共线组合；
 * 3. 返回值会克隆输入点，调用方可以安全地继续做坐标系投影计算。
 *
 * @param sampledPoints 圆边采样点。
 * @returns 拟合点三元组；无法找到非共线组合时返回 `null`。
 */
export function resolveCircularSolvePoints(
  sampledPoints: readonly THREE.Vector3[],
): [THREE.Vector3, THREE.Vector3, THREE.Vector3] | null {
  if (sampledPoints.length < 3) {
    return null;
  }

  const firstPoint = sampledPoints[0]?.clone() ?? null;
  if (!firstPoint) {
    return null;
  }

  // 从中间点和末端点组合中寻找非共线三点，避免重复采样点导致圆拟合退化。
  for (let middleIndex = 1; middleIndex < sampledPoints.length - 1; middleIndex += 1) {
    const middlePoint = sampledPoints[middleIndex]?.clone() ?? null;
    if (!middlePoint) {
      continue;
    }

    for (let lastIndex = sampledPoints.length - 1; lastIndex > middleIndex; lastIndex -= 1) {
      const lastPoint = sampledPoints[lastIndex]?.clone() ?? null;
      if (!lastPoint) {
        continue;
      }

      const candidateNormal = normalizeVec3(
        middlePoint.clone().sub(firstPoint).cross(lastPoint.clone().sub(firstPoint)),
      );
      if (candidateNormal) {
        return [firstPoint, middlePoint, lastPoint];
      }
    }
  }

  return null;
}
